import { isCancel, log, note, select, spinner } from '@clack/prompts';
import { Command } from 'commander';
import { spawn } from 'node:child_process';
import { existsSync, readFileSync } from 'node:fs';
import { chmod, mkdir, rm, writeFile } from 'node:fs/promises';
import { hostname, homedir, tmpdir } from 'node:os';
import { join } from 'node:path';
import { setTimeout as delay } from 'node:timers/promises';
import { loadEffectiveConfig, setConfigValue, unsetConfigValue } from '@agentbox/config';
import {
  drainCreateJobs,
  GitHubAppLeaser,
  loadGitHubAppConfig,
  parseGitRemote,
  PostgresStore,
  toAuthedHttpsUrl,
} from '@agentbox/relay';
import { randomBytes } from 'node:crypto';
import { providerForCreate } from '../provider/registry.js';
import { makeControlPlaneCreateBox } from '../control-plane/create-box.js';
import { runGitHubAppManifestFlow } from '../control-plane/github-app-manifest.js';
import { deployControlPlaneToVercel } from '../control-plane/deploy-vercel.js';
import { runHetznerDeploy } from '../control-plane/deploy-hetzner.js';
import { resolveHubAuthEnv } from '../control-plane/hub-auth-env.js';
import {
  addRepoUrl,
  checkRepoInstalled,
  loadControlPlaneMeta,
  openInBrowser,
  resolveOwnerRepo,
} from '../control-plane/ensure-repo-installed.js';
import { handleLifecycleError } from './_errors.js';

const CP_DIR = join(homedir(), '.agentbox', 'control-plane');
const PEM_PATH = join(CP_DIR, 'github-app.pem');
const ENV_PATH = join(CP_DIR, 'control-plane.env');
const META_PATH = join(CP_DIR, 'control-plane.json');

/** Default GitHub repo the control plane is deployed from (Vercel + Hetzner). */
const DEFAULT_DEPLOY_REPO = 'madarco/agentbox';
const DEFAULT_DEPLOY_REF = 'main';

/** Persist `relay.controlPlaneUrl` and report it. Shared by set-url + setup. */
async function applyControlPlaneUrl(url: string): Promise<string> {
  const trimmed = url.replace(/\/$/, '');
  await setConfigValue('global', 'relay.controlPlaneUrl', trimmed, process.cwd());
  return trimmed;
}

/** Poll a deployed plane's /healthz until it answers (or the deadline elapses). */
async function waitForHealthz(url: string, deadlineMs: number): Promise<boolean> {
  const stop = Date.now() + deadlineMs;
  while (Date.now() < stop) {
    try {
      const res = await fetch(`${url}/healthz`, { signal: AbortSignal.timeout(8000) });
      if (res.ok) return true;
    } catch {
      /* retry */
    }
    await delay(4000);
  }
  return false;
}

type DeployTarget = 'vercel' | 'hetzner' | 'none';

interface SetupOpts {
  name?: string;
  org?: string;
  deploy?: string;
  ref?: string;
  repo?: string;
}

const setupSub = new Command('setup')
  .description(
    'Create the GitHub App the control plane leases push tokens with, and write its deploy config (env + admin token)',
  )
  .option('--name <name>', 'GitHub App name (default: autogenerated agentbox-<rand>, must be globally unique)')
  .option('--org <org>', 'create the App under an organization instead of your user account')
  .option('--deploy <target>', 'deploy the plane after creating the App: vercel | hetzner | none')
  .option('--repo <owner/name>', 'GitHub repo to deploy the plane from (default madarco/agentbox; fork + pass this if you don\'t own it)')
  .option('--ref <ref>', 'git ref of the repo to deploy (branch/tag/sha; default main) — used by Vercel + Hetzner')
  .action(async (opts: SetupOpts) => {
    try {
      // Globally-unique by default — GitHub rejects duplicate App names.
      const name = opts.name ?? `agentbox-${randomBytes(4).toString('hex')}`;

      note(
        'Your browser will open to GitHub to create the App. Review the\n' +
          'permissions (Contents + Pull requests: write) and click "Create".',
        'GitHub App',
      );

      const s = spinner();
      s.start('waiting for the GitHub App to be created in your browser');
      const app = await runGitHubAppManifestFlow({
        appName: String(name),
        org: opts.org,
        // Honor GHES overrides (and make the flow scriptable in tests).
        githubUrl: process.env.GITHUB_URL,
        apiBaseUrl: process.env.GITHUB_API_URL,
        openBrowser: openInBrowser,
        log: (line) => s.message(line),
      });
      s.stop(`GitHub App created: ${app.slug} (id ${app.appId})`);

      // Persist the App key + a generated admin token as the plane's deploy env.
      const adminToken = randomBytes(32).toString('hex');
      await mkdir(CP_DIR, { recursive: true });
      await writeFile(PEM_PATH, app.pem, { mode: 0o600 });
      await chmod(PEM_PATH, 0o600);
      const pemB64 = Buffer.from(app.pem, 'utf8').toString('base64');
      const envBody =
        `# AgentBox control plane deploy env (generated by 'agentbox control-plane setup').\n` +
        `# Feed to docker compose (--env-file) or 'vercel env add'. Keep secret.\n` +
        `GITHUB_APP_ID=${app.appId}\n` +
        `GITHUB_APP_PRIVATE_KEY=${pemB64}\n` +
        `AGENTBOX_RELAY_ADMIN_TOKEN=${adminToken}\n`;
      await writeFile(ENV_PATH, envBody, { mode: 0o600 });
      await chmod(ENV_PATH, 0o600);
      await writeFile(
        META_PATH,
        JSON.stringify({ appId: app.appId, slug: app.slug, htmlUrl: app.htmlUrl, installUrl: app.installUrl }, null, 2) + '\n',
        { mode: 0o600 },
      );

      log.success(`GitHub App ready. Deploy config written to ${ENV_PATH} (0600).`);

      // --- Deploy step (after the App exists) ---
      const target = await resolveDeployTarget(opts.deploy);
      let deployedUrl: string | null = null;
      if (target !== 'none') {
        // Prompt for the hub login admin BEFORE the deploy spinner starts (a
        // spinner and @clack prompts can't share the terminal). Setting these
        // turns login on in the deployed hub so it is never left loginless — both
        // vercel and hetzner (the Caddy-HTTPS docker-compose deploy).
        const hubAuth = await resolveHubAuthEnv();
        // hetzner reads ENV_PATH (written to the VPS .env); append the auth env +
        // the Postgres auth profile so docker-compose enforces login there too.
        if (target === 'hetzner' && hubAuth) {
          const authBody =
            `AGENTBOX_HUB_PROFILE=vercel\n` +
            `AGENTBOX_HUB_AUTH=${hubAuth.AGENTBOX_HUB_AUTH}\n` +
            `BETTER_AUTH_SECRET=${hubAuth.BETTER_AUTH_SECRET}\n` +
            `AGENTBOX_HUB_ADMIN_EMAIL=${hubAuth.AGENTBOX_HUB_ADMIN_EMAIL}\n` +
            `AGENTBOX_HUB_ADMIN_PASSWORD=${hubAuth.AGENTBOX_HUB_ADMIN_PASSWORD}\n`;
          await writeFile(ENV_PATH, envBody + authBody, { mode: 0o600 });
          await chmod(ENV_PATH, 0o600);
        }
        try {
          const ds = spinner();
          ds.start(`deploying the control plane to ${target}`);
          const onLog = (line: string): void => ds.message(line);
          const repo = opts.repo ?? DEFAULT_DEPLOY_REPO;
          const ref = opts.ref ?? DEFAULT_DEPLOY_REF;
          if (target === 'vercel') {
            const env = {
              GITHUB_APP_ID: app.appId,
              GITHUB_APP_PRIVATE_KEY: pemB64,
              AGENTBOX_RELAY_ADMIN_TOKEN: adminToken,
              ...(hubAuth ?? {}),
            };
            deployedUrl = (await deployControlPlaneToVercel({ env, repo, ref, log: onLog })).url;
          } else {
            deployedUrl = (await runHetznerDeploy({ envPath: ENV_PATH, repoRef: ref, log: onLog })).url;
          }
          ds.stop(`deployed: ${deployedUrl}`);
        } catch (e) {
          log.warn(`deploy to ${target} failed: ${e instanceof Error ? e.message : String(e)}`);
          printManualDeploy();
        }
      } else {
        printManualDeploy();
      }

      if (deployedUrl) {
        const url = await applyControlPlaneUrl(deployedUrl);
        log.success(`Pointed the CLI at ${url} (relay.controlPlaneUrl).`);
        const ok = await waitForHealthz(url, 60_000);
        log[ok ? 'success' : 'warn'](
          ok ? `Control plane is healthy (${url}/healthz).` : `Could not confirm ${url}/healthz yet — check the deployment.`,
        );
      }

      // Open the repo-selection page so the user can authorize repos right away.
      log.info(`Install the App on your repos: ${app.installUrl}`);
      openInBrowser(app.installUrl);
    } catch (err) {
      handleLifecycleError(err);
    }
  });

/** Resolve the deploy target from the flag, or ask interactively. */
async function resolveDeployTarget(flag: string | undefined): Promise<DeployTarget> {
  if (flag === 'vercel' || flag === 'hetzner' || flag === 'none') return flag;
  if (flag) {
    log.warn(`unknown --deploy "${flag}"; choose interactively`);
  }
  const choice = await select({
    message: 'Deploy the control plane now?',
    options: [
      { value: 'vercel', label: 'Vercel — managed (auto-provisions Neon Postgres)' },
      { value: 'hetzner', label: 'Hetzner VPS — HTTPS via <ip>.sslip.io + Caddy/Let’s Encrypt' },
      { value: 'none', label: 'Skip — I’ll deploy later (print manual steps)' },
    ],
    initialValue: 'vercel',
  });
  if (isCancel(choice)) return 'none';
  return choice as DeployTarget;
}

function printManualDeploy(): void {
  log.info(
    [
      'Deploy later with the written env:',
      `  Self-host:  cd apps/hub && docker compose --env-file ${ENV_PATH} up --build`,
      `  Vercel:     agentbox control-plane setup --deploy vercel  (or 'vercel env add' the vars + 'vercel deploy --prod')`,
      `  Then:       agentbox control-plane set-url https://<your-plane-url>`,
    ].join('\n'),
  );
}

const setUrlSub = new Command('set-url')
  .description('Point boxes + the CLI at a deployed control plane (sets relay.controlPlaneUrl)')
  .argument('<url>', 'base URL of the deployed control plane, e.g. https://plane.example.com')
  .action(async (url: string) => {
    try {
      const trimmed = await applyControlPlaneUrl(url);
      log.success(`Set relay.controlPlaneUrl = ${trimmed}`);
    } catch (err) {
      handleLifecycleError(err);
    }
  });

const unsetUrlSub = new Command('unset-url')
  .description('Stop using a control plane on this machine (removes relay.controlPlaneUrl)')
  .option('--purge', "also delete this machine's local App metadata + admin token (~/.agentbox/control-plane)")
  .action(async (opts: { purge?: boolean }) => {
    try {
      // set-url writes the global scope; also clear a per-project override if one
      // exists. A cwd with no project config just yields `existed:false` (the
      // project unset never throws for that) — real I/O/write errors from either
      // scope propagate to handleLifecycleError rather than being masked.
      const g = await unsetConfigValue('global', 'relay.controlPlaneUrl', process.cwd());
      const p = await unsetConfigValue('project', 'relay.controlPlaneUrl', process.cwd());
      if (!g.existed && !p.existed) {
        log.info('No control plane was configured (relay.controlPlaneUrl not set).');
      } else {
        const where = [g.existed ? 'global' : null, p.existed ? 'project' : null].filter(Boolean).join(' + ');
        log.success(
          `Removed relay.controlPlaneUrl (${where}). New cloud boxes now push through the host relay ` +
            '(your laptop must be on), and the GitHub-App repo-authorization prompt no longer fires.',
        );
      }
      if (existsSync(CP_DIR)) {
        if (opts.purge) {
          await rm(CP_DIR, { recursive: true, force: true });
          log.success(`Purged this machine's control-plane credentials (${CP_DIR}).`);
        } else {
          log.info(`This machine's App metadata + admin token remain in ${CP_DIR} (use --purge to delete).`);
        }
      }
    } catch (err) {
      handleLifecycleError(err);
    }
  });

interface StatusOpts {
  url?: string;
  json?: boolean;
}

const statusSub = new Command('status')
  .description('Check whether the configured control plane is reachable')
  .option('--url <url>', 'override the control-plane URL (default: relay.controlPlaneUrl)')
  .option('--json', 'emit JSON')
  .action(async (opts: StatusOpts) => {
    try {
      const { loadEffectiveConfig } = await import('@agentbox/config');
      const cfg = await loadEffectiveConfig(process.cwd());
      const url = (opts.url ?? cfg.effective.relay.controlPlaneUrl ?? '').replace(/\/$/, '');
      if (!url) {
        log.error('No control plane configured. Run `agentbox control-plane set-url <url>` (or pass --url).');
        process.exitCode = 1;
        return;
      }
      let healthy = false;
      let detail = '';
      try {
        const res = await fetch(`${url}/healthz`, { signal: AbortSignal.timeout(8000) });
        healthy = res.ok;
        const body = (await res.json().catch(() => ({}))) as { boxes?: number; events?: number };
        detail = `${String(body.boxes ?? 0)} box(es), ${String(body.events ?? 0)} event(s)`;
      } catch (e) {
        detail = e instanceof Error ? e.name : String(e);
      }
      if (opts.json) {
        process.stdout.write(JSON.stringify({ url, healthy, detail }) + '\n');
        return;
      }
      process.stdout.write(
        [`control plane: ${healthy ? 'reachable' : 'UNREACHABLE'}`, `  url:    ${url}`, `  health: ${detail}`].join('\n') +
          '\n',
      );
    } catch (err) {
      handleLifecycleError(err);
    }
  });

const addSub = new Command('add')
  .description("Authorize the current project's git repo on the control plane's GitHub App")
  .action(async () => {
    try {
      const cfg = await loadEffectiveConfig(process.cwd());
      const controlPlaneUrl = cfg.effective.relay.controlPlaneUrl;
      if (!controlPlaneUrl) {
        log.error('No control plane configured. Run `agentbox control-plane set-url <url>` first.');
        process.exitCode = 1;
        return;
      }
      const ownerRepo = await resolveOwnerRepo(process.cwd());
      if (!ownerRepo) {
        log.error('No git origin found here — `cd` into a project with a GitHub remote.');
        process.exitCode = 1;
        return;
      }
      const { owner, repo } = ownerRepo;
      const installed = await checkRepoInstalled(owner, repo, controlPlaneUrl);
      if (installed === true) {
        log.success(`${owner}/${repo} is already authorized on the GitHub App.`);
        return;
      }
      const url = addRepoUrl(loadControlPlaneMeta());
      if (!url) {
        log.error(
          'No GitHub App metadata found locally. Run this from a machine that ran `control-plane setup`,\n' +
            'or open your App settings on GitHub and add the repo manually.',
        );
        process.exitCode = 1;
        return;
      }
      log.info(`Opening ${url} — select ${owner}/${repo}, then approve.`);
      openInBrowser(url);
    } catch (err) {
      handleLifecycleError(err);
    }
  });

/** Load the setup-written App creds into the env if not already set. */
function loadControlPlaneEnv(): void {
  if (process.env.GITHUB_APP_ID && process.env.GITHUB_APP_PRIVATE_KEY) return;
  if (!existsSync(ENV_PATH)) return;
  for (const line of readFileSync(ENV_PATH, 'utf8').split('\n')) {
    const m = /^([A-Z_]+)=(.*)$/.exec(line.trim());
    if (m && !process.env[m[1]!]) process.env[m[1]!] = m[2];
  }
}

/** Run `git <args>`, rejecting on a non-zero exit. */
function runGit(args: string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn('git', args, { stdio: ['ignore', 'ignore', 'pipe'] });
    let err = '';
    child.stderr.on('data', (c: Buffer) => (err += c.toString('utf8')));
    child.on('error', reject);
    child.on('close', (code) =>
      code === 0 ? resolve() : reject(new Error(`git ${args[0]} failed (${String(code)}): ${err.trim()}`)),
    );
  });
}

interface WorkerOpts {
  store: string;
  once?: boolean;
  pollInterval: string;
}

const workerSub = new Command('worker')
  .description(
    'Drain the control plane box-creation queue and provision real boxes (long-running; needs provider creds + the App key)',
  )
  .requiredOption('--store <url>', 'Postgres URL of the control plane (same store the plane uses)')
  .option('--once', 'drain the queue once and exit (default: loop)')
  .option('--poll-interval <ms>', 'poll interval when looping', '5000')
  .action(async (opts: WorkerOpts) => {
    try {
      loadControlPlaneEnv();
      const appCfg = loadGitHubAppConfig();
      if (!appCfg) {
        log.error('No GitHub App configured. Run `agentbox control-plane setup` first.');
        process.exitCode = 1;
        return;
      }
      const store = new PostgresStore({ connectionString: opts.store });
      await store.migrate();
      const leaser = new GitHubAppLeaser(appCfg);
      const cfg = await loadEffectiveConfig(process.cwd());

      const createBox = makeControlPlaneCreateBox({
        leaseRemoteUrl: async (repoUrl) => {
          const { path } = parseGitRemote(repoUrl);
          const [owner, repo] = path.replace(/\.git$/, '').split('/');
          if (!owner || !repo) throw new Error(`cannot derive owner/repo from ${repoUrl}`);
          const { token } = await leaser.leaseRepoToken(owner, repo);
          return toAuthedHttpsUrl(repoUrl, token);
        },
        cloneRepo: async (authedUrl, repoUrl, dest, branch) => {
          await runGit(branch ? ['clone', '--branch', branch, authedUrl, dest] : ['clone', authedUrl, dest]);
          // Scrub the leased token: leave the box pointing at the bare origin.
          await runGit(['-C', dest, 'remote', 'set-url', 'origin', repoUrl]);
        },
        createBox: async ({ workspacePath, name, provider, onLog }) => {
          const p = await providerForCreate({ flag: provider, config: cfg.effective });
          const created = await p.create({ workspacePath, name, projectRoot: workspacePath, onLog });
          return { id: created.record.id };
        },
        tmpDir: (jobId) => join(tmpdir(), `agentbox-cp-worker-${jobId}`),
        cleanup: (dir) => rm(dir, { recursive: true, force: true }),
        log: (line) => process.stdout.write(`agentbox-cp-worker: ${line}\n`),
      });

      const workerId = `worker-${hostname()}-${String(process.pid)}`;
      const interval = Number.parseInt(opts.pollInterval, 10) || 5000;
      let running = true;
      const stop = (): void => {
        running = false;
      };
      process.on('SIGINT', stop);
      process.on('SIGTERM', stop);
      process.stdout.write(`agentbox-cp-worker: draining create jobs as ${workerId}\n`);
      do {
        const n = await drainCreateJobs(store, createBox, workerId);
        if (n > 0) process.stdout.write(`agentbox-cp-worker: processed ${String(n)} job(s)\n`);
        if (opts.once) break;
        await delay(interval);
      } while (running);
      await store.close();
    } catch (err) {
      handleLifecycleError(err);
    }
  });

export const controlPlaneCommand = new Command('control-plane')
  .description('EXPERIMENTAL - Set up + manage the hosted control plane (GitHub App, deploy config, reachability)')
  .addCommand(statusSub, { isDefault: true })
  .addCommand(setupSub)
  .addCommand(setUrlSub)
  .addCommand(unsetUrlSub)
  .addCommand(addSub)
  .addCommand(workerSub);
